import { useState, useMemo } from 'react';
import { type UseFormSetError } from 'react-hook-form';
import {
  useCategoryTypes,
  useCreateCategoryType,
  useUpdateCategoryType,
  useDeleteCategoryType,
  useCategoryTypeById,
} from '../../../hooks/queries/useVisitorManagement';
import type {
  VisitorCategoryType,
  VisitorCategoryTypeWrite,
} from '../../../types/VisitorManagementTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import { StatusBadge, type Column, type StatusType } from '@themes/aegix';
import { useSearchParams } from 'react-router-dom';

interface CategoryRow {
  [key: string]: unknown;
  id: string;
  name: string;
  code: string;
  requires_background_check: boolean;
  auto_detect_from_sis: boolean;
  is_active: boolean;
}

const columns: Column<CategoryRow>[] = [
  { key: 'name', label: 'Name', sortable: true, width: '180px' },
  { key: 'code', label: 'Code', sortable: true, width: '120px' },
  {
    key: 'requires_background_check',
    label: 'Background Check',
    width: '140px',
    render: (row) =>
      row.requires_background_check ? (
        <StatusBadge status={'warning' as StatusType} label="Required" />
      ) : null,
  },
  {
    key: 'auto_detect_from_sis',
    label: 'SIS Auto-Detect',
    width: '130px',
    render: (row) =>
      row.auto_detect_from_sis ? (
        <StatusBadge status={'active' as StatusType} label="Yes" />
      ) : null,
  },
  {
    key: 'is_active',
    label: 'Active',
    width: '100px',
    render: (row) => (
      <StatusBadge
        status={
          row.is_active ? ('active' as StatusType) : ('inactive' as StatusType)
        }
        label={row.is_active ? 'Active' : 'Inactive'}
      />
    ),
  },
];

export function useCategoryTypeCrud() {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('public_id');
  const urlTab = searchParams.get('subtab');
  const { data, isLoading, error } = useCategoryTypes();
  const isCategoryTab =
    urlTab?.toLowerCase().replace(/\s+/g, '-') === 'categories';
  const { data: categoryById, error: categoryByIdError } = useCategoryTypeById(
    urlId as string,
    isCategoryTab,
  );
  const createMutation = useCreateCategoryType();
  const updateMutation = useUpdateCategoryType();
  const deleteMutation = useDeleteCategoryType();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editItem, setEditItem] = useState<VisitorCategoryType | null>(null);
  const isSingleCategoryTypeView = Boolean(
    urlId &&
    urlTab?.toLowerCase().replace(/\s+/g, '-') === 'categories' &&
    categoryById &&
    !categoryByIdError,
  );
  const categoryDetails =
    isSingleCategoryTypeView && categoryById ? [categoryById] : (data ?? []);
  const rows: CategoryRow[] = useMemo(
    () =>
      (categoryDetails ?? []).map((c) => ({
        id: c.public_id,
        name: c.name,
        code: c.code,
        requires_background_check: c.requires_background_check,
        auto_detect_from_sis: c.auto_detect_from_sis,
        is_active: c.is_active,
      })),
    [categoryDetails],
  );

  const handleClose = () => {
    setDrawerOpen(false);
    setEditItem(null);
  };

  const handleRowClick = (row: CategoryRow) => {
    const item = data?.find((c) => c.public_id === row.id);
    if (item) {
      setEditItem(item);
      setDrawerOpen(true);
    }
  };

  const handleSubmit = (
    values: VisitorCategoryTypeWrite,
    setError: UseFormSetError<VisitorCategoryTypeWrite>,
  ) => {
    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof VisitorCategoryTypeWrite, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: 'Save failed',
        description: err.message,
        type: 'error',
      });
    };

    if (editItem) {
      updateMutation.mutate(
        { publicId: editItem.public_id, data: values },
        {
          onSuccess: () => {
            toaster.create({ title: 'Category type updated', type: 'success' });
            handleClose();
          },
          onError,
        },
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          toaster.create({ title: 'Category type created', type: 'success' });
          handleClose();
        },
        onError,
      });
    }
  };

  const handleDelete = () => {
    if (!editItem) return;
    deleteMutation.mutate(editItem.public_id, {
      onSuccess: () => {
        toaster.create({ title: 'Category type deleted', type: 'success' });
        handleClose();
      },
      onError: (err) =>
        toaster.create({
          title: 'Delete failed',
          description: err.message,
          type: 'error',
        }),
    });
  };

  const initialValues: VisitorCategoryTypeWrite = useMemo(
    () =>
      editItem
        ? {
            name: editItem.name,
            code: editItem.code,
            description: editItem.description,
            requires_background_check: editItem.requires_background_check,
            auto_detect_from_sis: editItem.auto_detect_from_sis,
            is_active: editItem.is_active,
          }
        : {
            name: '',
            code: '',
            requires_background_check: false,
            auto_detect_from_sis: false,
            is_active: true,
          },
    [editItem],
  );

  return {
    data: categoryDetails,
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  };
}
